import React, { useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import { Alert, useTheme } from "@mui/material";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import whatsappIcon from "../../../assets/WhatsApp.svg-200x200.png";

import AuthLayout, { authInputSx, authFieldLabelSx, authButtonSx } from "components/AuthLayout";
import FlexBetween from "components/FlexBetween";
import { markSuccessfulLogin } from "api/config";

const baseUrl = process.env.REACT_APP_BASE_URL + "/public";

export default function ClientLogin({ updateCustomerState }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const [loginFailed, setLoginFailed] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);

    await axios
      .post(baseUrl + "/client/login", {
        whatsapp: data.get("whatsapp"),
        password: data.get("password"),
      }, {
        withCredentials: true // Enable cookies for authentication
      })
      .then((res) => {
        if (res && res.status === 200 && res.data.status === "ok") {
          setLoginFailed(false);
          console.log(res.data);

          // Mark successful login to prevent immediate logout
          markSuccessfulLogin();

          // Store customer data in localStorage (tokens are now in secure cookies)
          if (res.data.customer) {
            const sanitizedCustomer = { ...res.data.customer };
            delete sanitizedCustomer.password;
            delete sanitizedCustomer.token;
            localStorage.setItem("customer", JSON.stringify(sanitizedCustomer));

            // Update customer state in App.js
            updateCustomerState(res.data.customer);

            // Navigate to customer portal (user already authenticated with password)
            navigate("/customer-portal");
          }
        }
      })
      .catch(function (error) {
        console.log(error); // 401
        if (error.response.status === 401) {
          console.log("login failed");
          setLoginFailed(true);
        }
      });
  };

  React.useEffect(() => {
    // Check if customer is already authenticated by checking stored customer data
    const customerData = localStorage.getItem("customer");
    if (customerData) {
      try {
        const customer = JSON.parse(customerData);
        if (customer && customer._id) {
          navigate("/tracking");
        }
      } catch (error) {
        console.error('Error parsing stored customer data:', error);
        localStorage.removeItem("customer");
      }
    }
  }, [navigate]);

  const inputSx = authInputSx(theme);
  const fieldLabelSx = authFieldLabelSx(theme);

  const features = [
    t("clientLogin.feature1"),
    t("clientLogin.feature2"),
    t("clientLogin.feature3"),
  ];

  return (
    <AuthLayout tagline={t("clientLogin.tagline")} features={features}>
      <Box sx={{ mb: "34px" }}>
        <Typography sx={{ fontFamily: "'Space Grotesk', 'Noto Sans Lao', sans-serif", fontSize: 30, fontWeight: 700, color: "#1b1728", lineHeight: 1.2, mb: 1 }}>
          {t("clientLogin.title")}
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#9b8ab8", fontWeight: 500 }}>
          {t("clientLogin.subtitle")}
        </Typography>
      </Box>

      <Box component="form" noValidate onSubmit={handleSubmit}>
        <Box sx={{ mb: 2 }}>
          <Typography component="label" htmlFor="whatsapp" sx={fieldLabelSx}>
            {t("clientLogin.usernameLabel")}
          </Typography>
          <TextField
            required
            fullWidth
            id="whatsapp"
            name="whatsapp"
            placeholder={t("clientLogin.usernamePlaceholder")}
            autoComplete="whatsapp"
            autoFocus
            type="tel"
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              maxLength: 15,
            }}
            onInput={(e) => {
              e.target.value = e.target.value.replace(/[^0-9]/g, "");
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineOutlinedIcon sx={{ fontSize: 18, color: "#9d8bc4" }} />
                </InputAdornment>
              ),
            }}
            sx={inputSx}
          />
        </Box>

        <Box sx={{ mb: "14px" }}>
          <Typography component="label" htmlFor="password" sx={fieldLabelSx}>
            {t("clientLogin.passwordLabel")}
          </Typography>
          <TextField
            required
            fullWidth
            id="password"
            name="password"
            placeholder={t("clientLogin.passwordPlaceholder")}
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            onChange={() => setLoginFailed(false)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ fontSize: 18, color: "#9d8bc4" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? (
                      <VisibilityOffOutlinedIcon sx={{ fontSize: 18, color: "#9d8bc4" }} />
                    ) : (
                      <VisibilityOutlinedIcon sx={{ fontSize: 18, color: "#9d8bc4" }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={inputSx}
          />
        </Box>

        {loginFailed && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t("clientLogin.invalidCredentials")}
          </Alert>
        )}

        <FlexBetween sx={{ mb: 3 }}>
          <Link
            component="button"
            type="button"
            variant="body2"
            sx={{
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "none",
              color: theme.palette.secondary.main,
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={(e) => {
              e.preventDefault();
              navigate("/forgot-password");
            }}
          >
            {t("clientLogin.forgotPassword")}
          </Link>
          <Link
            component="button"
            type="button"
            variant="body2"
            sx={{
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "none",
              color: theme.palette.secondary.main,
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={(e) => {
              e.preventDefault();
              navigate("/signup");
            }}
          >
            {t("clientLogin.signUp")}
          </Link>
        </FlexBetween>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="secondary"
          sx={{ ...authButtonSx(theme), mb: "22px" }}
        >
          {t("clientLogin.signIn").toUpperCase()}
        </Button>

        <Box sx={{ display: "flex", alignItems: "center", gap: "14px", mb: "18px" }}>
          <Divider sx={{ flex: 1, borderColor: "#ede9f6" }} />
          <Typography sx={{ fontSize: 12.5, color: "#b8aece", fontWeight: 500 }}>{t("clientLogin.or")}</Typography>
          <Divider sx={{ flex: 1, borderColor: "#ede9f6" }} />
        </Box>

        <Button
          fullWidth
          variant="outlined"
          color="secondary"
          onClick={(e) => {
            e.preventDefault();
            navigate("/whatsapp-login");
          }}
          startIcon={<Box component="img" src={whatsappIcon} alt="" sx={{ width: 22, height: 22 }} />}
          sx={{
            height: 52,
            borderRadius: "14px",
            borderColor: "#e5dff5",
            color: "#1b1728",
            fontSize: 14,
            fontWeight: 700,
            "&:hover": { borderColor: "#25d366", backgroundColor: "#f3fff6" },
          }}
        >
          {t("clientLogin.signInWithWhatsapp")}
        </Button>
      </Box>
    </AuthLayout>
  );
}
